import { useEffect, useMemo, useReducer, useRef } from 'react';
import { addError } from '@shared/utils/logger';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { useUpdateEffect } from '@shared/utils/hooks/useUpdateEffect';
import type { Maybe } from '@graphql/generated';
import { AutopopulateJobStatusV5 } from '@graphql/generated';
import { usePolling } from '@shared/hooks/usePolling';
import type { AutoPopulationPollingStatus, UseAutoPopulationPollingOptions, UseAutoPopulationPollingReturn } from './useAutoPopulation.types';

/**
 * Default poll schedule in milliseconds.
 * Total time: 300 seconds (5 minutes).
 * Schedule: 5s, 10s, 20s, 30s, 60s, 90s, 85s
 */
export const DEFAULT_POLL_SCHEDULE_MS = [5000, 10_000, 20_000, 30_000, 60_000, 90_000, 85_000] as const;

interface State<TResult>
  extends Readonly<{
    status: AutoPopulationPollingStatus;
    results: ReadonlyArray<TResult>;
    loading: boolean;
    error: Maybe<string>;
  }> {}

interface ProcessedData<TResult>
  extends Readonly<{
    status: AutopopulateJobStatusV5;
    results: ReadonlyArray<TResult>;
  }> {}

type Action<TResult> = Readonly<{ type: 'POLL_START' } | Readonly<{ type: 'UPDATE'; payload: Partial<State<TResult>> }> | Readonly<{ type: 'RESET' }>>;

const createInitialState = <TResult>(): State<TResult> =>
  ({
    status: 'IDLE',
    results: [],
    loading: false,
    error: null,
  }) as const;

/**
 * Returns true if two states are meaningfully identical, avoiding unnecessary re-renders.
 * Uses reference equality for results array, with a fast-path for both-empty.
 */
const isStateEqual = <TResult>(a: State<TResult>, b: State<TResult>): boolean =>
  a.status === b.status && a.loading === b.loading && a.error === b.error && (a.results === b.results || (a.results.length === 0 && b.results.length === 0));

const createReducer =
  <TResult>() =>
  (state: State<TResult>, action: Action<TResult>): State<TResult> => {
    switch (action.type) {
      case 'POLL_START': {
        const next: State<TResult> = { status: 'PENDING', results: [], loading: true, error: null };
        return isStateEqual(state, next) ? state : next;
      }
      case 'UPDATE': {
        const next: State<TResult> = { ...createInitialState<TResult>(), ...action.payload };
        return isStateEqual(state, next) ? state : next;
      }
      case 'RESET':
        return createInitialState<TResult>();
      default:
        return state;
    }
  };

const logError = <TContext>(message: string, context: TContext, eventId?: Maybe<string>) => {
  addError(new Error(message), {
    context,
    eventId,
    isAsync: true,
    operation: 'autoPopulationPolling',
  });
};

/**
 * Hook to poll auto-populate operations status.
 * This hook handles the polling logic and state management, while the specific
 * GraphQL query and result mapping are provided via configuration.
 */
export const useAutoPopulationPolling = <TData, TOperation, TResult, TContext>(
  options: UseAutoPopulationPollingOptions<TData, TOperation, TResult, TContext>,
): UseAutoPopulationPollingReturn<TResult> => {
  const { eventId, shouldSkipAutoPopulationCheck, context, queryResult, config, pollScheduleMs } = options;

  // Calculate max poll attempts: 1 immediate + scheduled intervals
  const maxPollAttempts = pollScheduleMs.length + 1;

  const [state, dispatch] = useReducer(createReducer<TResult>(), createInitialState<TResult>());
  const hasCompletedRef = useRef(false);
  const isMountedRef = useRef(true);
  const hasMaxAttemptsLoggedRef = useRef(false);

  // Query result is passed in from hooks called at the consumer level
  const { data, loading: queryLoading, error: apiError, refetch } = queryResult;

  // Reset on eventId change
  useEffect(() => {
    dispatch({ type: 'RESET' });
    hasCompletedRef.current = false;
    hasMaxAttemptsLoggedRef.current = false;
  }, [eventId]);

  // Track mount status
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Extract operations once — shared by processedData and operationsCount.
  const operations = useMemo(() => config.extractOperations(data), [data, config]);
  const operationsCount = operations?.length ?? 0;

  // Process operations data when query data changes
  const processedData = useMemo((): ProcessedData<TResult> => {
    // Check if any operation is PENDING
    const hasPendingOperation = operations?.some(operation => config.getOperationStatus(operation) === AutopopulateJobStatusV5.PENDING) ?? false;

    if (hasPendingOperation) {
      return { status: AutopopulateJobStatusV5.PENDING, results: [] };
    }

    // Collect results from COMPLETED operations only
    const completedResults =
      operations?.filter(operation => config.getOperationStatus(operation) === AutopopulateJobStatusV5.COMPLETED).flatMap(operation => config.mapOperationResults(operation)) ?? [];

    // If we have successful results, return them (prioritize partial success over failure)
    if (completedResults.length > 0) {
      return { status: AutopopulateJobStatusV5.COMPLETED, results: completedResults };
    }

    // No successful results: check if any operation failed
    const hasFailedOperation = operations?.some(operation => config.getOperationStatus(operation) === AutopopulateJobStatusV5.FAILED) ?? false;

    return {
      status: hasFailedOperation ? AutopopulateJobStatusV5.FAILED : AutopopulateJobStatusV5.COMPLETED,
      results: [],
    };
  }, [operations, config]);

  // Process and update state from query data
  useEffect(() => {
    if (!eventId || shouldSkipAutoPopulationCheck || !isMountedRef.current) return;

    // Handle errors
    if (apiError) {
      const errorMessage = apiError.message || 'Auto-population failed';
      dispatch({ type: 'UPDATE', payload: { status: 'FAILED', error: errorMessage } });
      logError(errorMessage, context, eventId);
      return;
    }

    // Handle operations status
    switch (processedData.status) {
      case AutopopulateJobStatusV5.PENDING:
        dispatch({ type: 'UPDATE', payload: { status: 'PENDING', loading: true } });
        return;
      case AutopopulateJobStatusV5.FAILED:
        dispatch({ type: 'UPDATE', payload: { status: 'FAILED', error: 'Auto-population operation failed' } });
        logError('Auto-population operation failed on backend', context, eventId);
        return;
      case AutopopulateJobStatusV5.COMPLETED:
        dispatch({ type: 'UPDATE', payload: { status: 'COMPLETED', results: processedData.results } });
        break;
    }
  }, [data, apiError, eventId, shouldSkipAutoPopulationCheck, processedData, context]);

  useEffect(() => {
    if (state.status !== 'COMPLETED' || hasCompletedRef.current || !eventId || !isMountedRef.current) return;
    hasCompletedRef.current = true;
  }, [state.status, eventId]);

  // Determine if polling should continue
  const shouldPoll = useEventCallback((pollCount: number) => {
    if (!eventId || shouldSkipAutoPopulationCheck || !isMountedRef.current) return false;

    // Stop polling after max attempts and log error once
    if (pollCount >= maxPollAttempts) {
      if (!hasMaxAttemptsLoggedRef.current && isMountedRef.current) {
        hasMaxAttemptsLoggedRef.current = true;
        const errorMessage = `Auto-population polling stopped after maximum attempts (${maxPollAttempts})`;
        dispatch({ type: 'UPDATE', payload: { status: 'FAILED', error: errorMessage } });
        logError(errorMessage, context, eventId);
      }
      return false;
    }

    const isProcessedDataCompleted = ['COMPLETED', 'FAILED'].includes(processedData?.status);
    const isStateCompleted = ['COMPLETED', 'FAILED'].includes(state?.status);
    const isOperationsCompleted = isProcessedDataCompleted || isStateCompleted;
    return !isOperationsCompleted;
  });

  // Poll for operations status
  const onPoll = useEventCallback(async () => {
    dispatch({ type: 'POLL_START' });
    await refetch();
  });

  // Handle polling error
  const onPollError = useEventCallback((error: unknown) => {
    if (!isMountedRef.current) return;
    const errorMessage = error instanceof Error ? error.message : 'Polling error occurred';
    dispatch({ type: 'UPDATE', payload: { status: 'FAILED', error: errorMessage } });
    logError(errorMessage, context, eventId);
  });

  // Handle polling timeout
  const onPollTimeout = useEventCallback(() => {
    if (!isMountedRef.current) return;
    if (!eventId || shouldSkipAutoPopulationCheck) return;
    dispatch({ type: 'UPDATE', payload: { status: 'FAILED', error: 'Polling timeout' } });
    logError('Auto-population polling timeout', context);
  });

  // Poll for operations status
  const { restart } = usePolling({
    shouldPoll,
    onPoll,
    onError: onPollError,
    onTimeout: onPollTimeout,
    pollScheduleMs,
    immediate: true,
  });

  // SPA navigation fix: when cache-and-network delivers fresh PENDING data after mount
  // (stale cache showed COMPLETED, usePolling already stopped), restart polling.
  // useUpdateEffect skips the initial render, so this only fires on post-mount data changes.
  useUpdateEffect(() => {
    if (!eventId || shouldSkipAutoPopulationCheck || !isMountedRef.current) return;
    if (processedData.status === AutopopulateJobStatusV5.PENDING) {
      hasCompletedRef.current = false;
      hasMaxAttemptsLoggedRef.current = false;
      restart();
    }
  }, [processedData.status, eventId, shouldSkipAutoPopulationCheck, restart]);

  // Reset state when restarting polling
  const restartPolling = useEventCallback(async () => {
    if (!eventId || shouldSkipAutoPopulationCheck || !isMountedRef.current) return;

    dispatch({ type: 'RESET' });
    hasCompletedRef.current = false;
    hasMaxAttemptsLoggedRef.current = false;

    await refetch();
    restart();
  });

  return {
    status: state.status,
    results: state.results,
    count: state.results.length,
    // Only report loading from active polling (POLL_START action), not from Apollo query loading.
    loading: state.loading,
    // Expose Apollo query loading so consumers can distinguish "no data yet" from "truly COMPLETED with no operations".
    queryLoading,
    error: state.error,
    // Synchronous count of operations from the latest query data (via useMemo).
    // Unlike status (updated asynchronously via effects), this reflects data immediately
    // during render — safe for guards that need to know if auto-population was ever triggered.
    operationsCount,
    restartPolling,
  };
};
